import React, { createContext, useMemo, useState } from "react";

import { useClients } from "hooks/api/use-clients";

import {
  SmsSimulatorSimulationAttributes,
  SmsSimulatorFormDraft,
} from "containers/sms-simulator/sms-simulator.types";

interface SmsSimulatorContextType {
  clients: { label: string; value: string }[];
  currentSimulation: SmsSimulatorSimulationAttributes | null;
  setCurrentSimulation: React.Dispatch<
    React.SetStateAction<SmsSimulatorSimulationAttributes | null>
  >;
  simulatorFormDraft: SmsSimulatorFormDraft;
  setSimulatorFormDraft: React.Dispatch<React.SetStateAction<SmsSimulatorFormDraft>>;
}

export const SmsSimulatorContext = createContext<SmsSimulatorContextType | undefined>(undefined);

export const SmsSimulatorProvider = (props: React.PropsWithChildren) => {
  const [currentSimulation, setCurrentSimulation] =
    useState<SmsSimulatorSimulationAttributes | null>(null);
  const [simulatorFormDraft, setSimulatorFormDraft] = useState<SmsSimulatorFormDraft>({});

  const { data: clientsResponse } = useClients({});
  const clients = useMemo(
    () =>
      (clientsResponse?.items ?? []).map((client) => ({
        label: client.name,
        value: client.id,
      })),
    [clientsResponse],
  );

  const value = useMemo(
    () => ({
      clients,
      currentSimulation,
      setCurrentSimulation,
      simulatorFormDraft,
      setSimulatorFormDraft,
    }),
    [clients, currentSimulation, simulatorFormDraft],
  );

  return <SmsSimulatorContext.Provider value={value} {...props} />;
};

export const useSmsSimulatorContext = () => {
  const context = React.useContext(SmsSimulatorContext);
  if (context === undefined) {
    throw new Error("useSmsSimulatorContext must be used within a SmsSimulatorProvider");
  }
  return context;
};
